import { useState, useRef } from 'react';
import { toast } from '@/lib/toast';
import { isValidUrl, getUrlValidationError } from '../lib/businessProfileUtils';
import type { UsageResponse } from '@/types';

interface BusinessInfoResult {
  description_suggestion: string;
  products_services_suggestion: string;
}

interface UseBusinessInfoGeneratorProps {
  formData: {
    description?: string;
    products_services?: string[];
    website_url?: string;
  };
  setFormData: (updater: (prev: any) => any) => void;
  onGenerateBusinessInfo?: (url: string) => Promise<BusinessInfoResult & { raw_context: string }>;
  usage?: UsageResponse;
}

export function useBusinessInfoGenerator({ formData, setFormData, onGenerateBusinessInfo, usage }: UseBusinessInfoGeneratorProps) {
  const [generating, setGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [generationResult, setGenerationResult] = useState<BusinessInfoResult | null>(null);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const errorSetRef = useRef(false);

  const applyBusinessInfoResults = async (result: BusinessInfoResult) => {
    let descriptionUpdated = false;
    let productsAdded = 0;

    // Auto-populate description (replace if exists)
    if (result.description_suggestion) {
      setFormData((prev: any) => ({
        ...prev,
        description: result.description_suggestion,
      }));
      descriptionUpdated = true;
    }
    
    // Auto-populate products/services as tags (append new ones only, max 10 total)
    if (result.products_services_suggestion) {
      const MAX_PRODUCTS_SERVICES = 10;
      const newProducts = result.products_services_suggestion.split(',').map((p: string) => p.trim()).filter((p: string) => p);
      const existingProducts = Array.isArray(formData.products_services) 
        ? formData.products_services.map((p: string) => p.toLowerCase().trim())
        : [];
      
      // Filter out duplicates
      const productsToAdd = newProducts.filter((product: string) => 
        !existingProducts.includes(product.toLowerCase().trim())
      );
      
      // Calculate how many we can add without exceeding the limit
      const currentCount = existingProducts.length;
      const availableSlots = MAX_PRODUCTS_SERVICES - currentCount;
      const productsToAddLimited = productsToAdd.slice(0, Math.max(0, availableSlots));
      
      if (productsToAddLimited.length > 0) {
        setFormData((prev: any) => ({
          ...prev,
          products_services: [...(prev.products_services || []), ...productsToAddLimited],
        }));
        productsAdded = productsToAddLimited.length;
        
        // Warn if some products were skipped due to limit
        if (productsToAdd.length > productsToAddLimited.length) {
          const skipped = productsToAdd.length - productsToAddLimited.length;
          toast.raw.warning(`${productsAdded} product${productsAdded > 1 ? 's' : ''} added. ${skipped} skipped (maximum ${MAX_PRODUCTS_SERVICES} allowed)`);
        }
      } else if (productsToAdd.length > 0) {
        // All products would exceed limit
        toast.raw.warning(`Maximum ${MAX_PRODUCTS_SERVICES} products/services allowed. Please remove some existing items to add new ones.`);
      }
    }

    // Show success message
    const updates: string[] = [];
    if (descriptionUpdated) updates.push('description');
    if (productsAdded > 0) updates.push(`${productsAdded} product${productsAdded > 1 ? 's' : ''}/service${productsAdded > 1 ? 's' : ''}`);
    
    if (updates.length > 0) {
      toast.raw.success(`Website information updated: ${updates.join(', ')}`);
    } else {
      toast.raw.info('No new information found to add');
    }
  };

  const handleGenerateBusinessInfo = async () => {
    if (!formData.website_url || !onGenerateBusinessInfo) {
      return;
    }

    // Validate URL format before making request
    const trimmedUrl = formData.website_url.trim();
    if (!isValidUrl(trimmedUrl)) {
      const errorMessage = getUrlValidationError(trimmedUrl) || 'Please enter a valid website URL';
      setGenerationError(errorMessage);
      return;
    }

    // Store URL for quota dialog
    setPendingUrl(trimmedUrl);
    
    // Show quota confirmation dialog before making API call
    setQuotaDialogOpen(true);
  };

  const handleQuotaConfirm = async () => {
    if (!pendingUrl || !onGenerateBusinessInfo) {
      setQuotaDialogOpen(false);
      return;
    }

    setQuotaDialogOpen(false);
    setGenerating(true);
    setGenerationError('');
    errorSetRef.current = false;
    try {
      const result = await onGenerateBusinessInfo(pendingUrl);
      
      // Check if we need to ask for confirmation
      const hasExistingDescription = formData.description && formData.description.trim().length > 0;
      const hasExistingProducts = formData.products_services && formData.products_services.length > 0;
      
      if (hasExistingDescription || hasExistingProducts) {
        // Store result and show confirmation dialog
        setGenerationResult(result);
        setConfirmDialogOpen(true);
      } else {
        // No existing data, apply directly
        await applyBusinessInfoResults(result);
      }
    } catch (error: any) {
      // Extract error message from backend response for inline Alert display
      let errorMessage = 'Failed to generate business information';
      
      if (error?.response?.data) {
        const data = error.response.data;
        
        // Handle FastAPI validation errors (array of error objects)
        if (Array.isArray(data)) {
          errorMessage = data.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err?.msg) return err.msg;
            if (err?.message) return err.message;
            return JSON.stringify(err);
          }).join(', ');
        } else if (typeof data === 'object' && data.detail) {
          // Backend returns sanitized error in data.detail
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            errorMessage = data.detail.map((err: any) => {
              if (typeof err === 'string') return err;
              if (err?.msg) return err.msg;
              if (err?.message) return err.message;
              return JSON.stringify(err);
            }).join(', ');
          }
        } else if (data.message && typeof data.message === 'string') {
          errorMessage = data.message;
        }
      } else if (error?.message && typeof error.message === 'string') {
        errorMessage = error.message;
      }
      
      // Only set error for inline Alert display - toast is handled by existing system
      if (!errorSetRef.current) {
        setGenerationError(errorMessage);
        errorSetRef.current = true;
      }
    } finally {
      setGenerating(false);
      setPendingUrl(null);
    }
  };

  const handleQuotaCancel = () => {
    setQuotaDialogOpen(false);
    setPendingUrl(null);
  };

  const handleConfirmReplace = async () => {
    if (generationResult) {
      await applyBusinessInfoResults(generationResult);
      setConfirmDialogOpen(false);
      setGenerationResult(null);
    }
  };

  const handleCancelReplace = () => {
    setConfirmDialogOpen(false);
    setGenerationResult(null);
  };

  return {
    generating,
    generationError,
    confirmDialogOpen,
    generationResult,
    quotaDialogOpen,
    pendingUrl,
    handleGenerateBusinessInfo,
    handleQuotaConfirm,
    handleQuotaCancel,
    handleConfirmReplace,
    handleCancelReplace,
  };
}

